// Vuex actions
import { transform, debounceSyncMem, logreject, guid } from './helpers'
import api from 'js/api/resources'

/**
 * Resets all modules
 */
export const reset = ({dispatch}) => {
    dispatch('RESET_ALL')
}

export const fetchAssignment = ({dispatch, state}, id, includeUserProgress) => {

    const cached = state.assignments.storage[id];

    if ( cached !== undefined && cached.loaded === true && includeUserProgress == null ) {

        // check not really necessary: does not trigger getters
        if ( state.assignments.currentId !== cached.id ) {
            dispatch('SET_CURRENT_ASSIGNMENT', cached);
        }

        return Promise.resolve( cached );

    }

    return api.getAssignment(id, includeUserProgress)
        .then( response => {

            let result = transform(response.body);

            // mark assignments as fully loaded
            for( let r of result.assignments) {
                r.loaded = true;
            }

            // TODO: should check if all resources are received
            dispatch('ADD_ASSIGNMENTS', result.assignments || []);
            dispatch('ADD_STEPS', result.steps || []);
            dispatch('ADD_PARTIALS', result.partials || []);

            storeProgress({ dispatch }, result)

            dispatch('ADD_USERS', result.users || [])

            dispatch('SET_CURRENT_ASSIGNMENT', result.assignments[0]);

            return result.assignments[0]

        })
        .catch(error => logreject('Could not fetch assignments', error))

}


/**
 * Fetches the term data for the given group term id
 * checks if the given periodId is included in the response
 * If not => fetch the term data for the given periodId as well
 *
 * @param  {String} id                  Id of the requested GroupTerm
 * @param  {String} includeUserProgress user id of progress to include in response
 * @param  {String} GroupPeriodId            GroupPeriod that must be included in the data
 * @return {Promise}                    Retrieved term objects
 */
export const fetchGroupTerm = ({dispatch, state}, id, includeUserProgress, groupPeriodId) => {

    const storeResources =  response => {

        let result = transform(response.body)

        dispatch('ADD_GROUP_TERMS', result.groupterms || []);
        dispatch('ADD_GROUP_PERIODS', result.groupperiods || []);
        dispatch('ADD_GROUP_ASSIGNMENTS', result.groupassignments || []);

        dispatch('ADD_TERMS', result.terms || []);
        dispatch('ADD_PERIODS', result.periods || []);

        dispatch('ADD_ASSIGNMENTS', result.assignments || []);
        dispatch('ADD_STEPS', result.steps || []);

        if ( includeUserProgress != null ) {
            storeProgress({ dispatch }, result)
        }

        return result

    }

    return api.getTerm(id, includeUserProgress)
        .then( storeResources )
        .then( result => {

            // check if we loaded the period as well, otherwise fetch the corresponding term
            const loadedGroupPeriods = result.groupperiods.map( p => p.id)

            // TODO: change to separate fetchGroupPeriod ?
            if ( groupPeriodId != null && loadedGroupPeriods.indexOf(groupPeriodId) === -1 ) {
                return api.getTermForPeriod(groupPeriodId, includeUserProgress)
                    .then( storeResources )
            }

            return result

        })
        .catch( error => logreject('Could not fetch term', error))

}

export const fetchUser = ({ dispatch, state }, id, includeEssentials, beforeFetchHook) => {

    if ( id == null ) {
        return Promise.reject('No user id provided')
    }

    // first check if the user is already fetched
    const cached = state.users.storage[id];
    if ( cached != null ) {
        return Promise.resolve( cached )
    }

    if (beforeFetchHook) {
        beforeFetchHook()
    }

    return api.getUser( id, includeEssentials )
        .then( response => storeUserResponse({ dispatch }, response, includeEssentials) )
        .catch( error => logreject('Could not fetch user', error) )

}

export const storeUserResponse = ({ dispatch }, response, includedEssentials) => {

    const result = transform(response.body)
    const user = result.users[0]
    dispatch('SET_CURRENT_USER', user)
    dispatch('ADD_USERS', result.users || [])

    if ( includedEssentials ) {
        dispatch('ADD_SCHOOLS', result.schools || [])

        dispatch('ADD_GROUPS', result.groups || [])
        dispatch('ADD_GROUP_TERMS', result.groupterms || [])
        dispatch('ADD_GROUP_PERIODS', result.groupperiods || [])
        dispatch('ADD_GROUP_ASSIGNMENTS', result.groupassignments || [])

        dispatch('ADD_TERMS', result.terms || [])
        dispatch('ADD_PERIODS', result.periods || [])

        // below only for student
        dispatch('ADD_ASSIGNMENTS', result.assignments || [])
        dispatch('ADD_STEPS', result.steps || [])

        storeProgress({ dispatch }, result)
    }

    return user
}

const storeProgress = ({ dispatch }, result) => {

    dispatch('ADD_PROGRESS', result.progress || [])
    dispatch('ADD_SUBMISSIONS', result.submissions || [])

    dispatch('ADD_FEEDBACK', result.feedback || []);
    dispatch('ADD_REMARKS', result.remarks || []);
    dispatch('ADD_COMPLIMENTS', result.compliments || []);
    dispatch('ADD_DILIGENCE', result.diligence || []);
    dispatch('ADD_BADGES', result.badges || []);
}


export const setValidation = ({dispatch}, partialId, validated) => {
    dispatch('VALIDATE_PARTIAL', partialId, validated)
}



/**
 * Stores data to vuex, then calls debounced sync to API
 *
 * @param {Vuex}
 * @param {Object} partial
 * @param {Object} progress
 * @param {Object} data     submission data
 */
export const setSubmission = ({ dispatch, state }, partial, progress, revision, data) => {

    const oldVal = getExistingSubmission({ state }, partial, progress, revision)

    if ( oldVal == null ) {
        addTempSubmission({ dispatch }, partial, progress, revision, data)
    } else {
        dispatch('UPDATE_SUBMISSION', oldVal.id, data);
    }

    return syncSubmission({ dispatch, state }, partial, progress, revision, data)

}

const syncSubmission = debounceSyncMem( ({ dispatch, state }, partial, progress, revision, data) => {

    const oldVal = getExistingSubmission({ state }, partial, progress, revision)

    if ( oldVal == null || oldVal._temp ) {

        return api.addSubmission(data, partial.id)
            .then( response => transform(response.body) )
            .then( result => {
                let newSubmission = result.submissions[0]

                // might be updated in the mean time, oldVal is reactive
                newSubmission.data = oldVal.data

                dispatch('SET_SUBMISSION', newSubmission)
                dispatch('SWAP_SUBMISSION_ID', partial.id, oldVal.id, newSubmission.id)
                dispatch('REMOVE_SUBMISSION', oldVal.id)

                if ( partial.primary ) {
                    dispatch('ADD_PRIMARY_SUBMISSION_TO_PROGRESS', progress.id, newSubmission.id)
                    dispatch('REMOVE_PRIMARY_SUBMISSION_FROM_PROGRESS', progress.id, oldVal.id)
                }

            })

    }

    return api.patchSubmission(oldVal)
      .then( response => transform(response.body) )
      .then( result => {
          let newSubmission = result.submissions[0]

          // might be updated in the mean time, oldVal is reactive
          newSubmission.data = oldVal.data

          dispatch('SET_SUBMISSION', newSubmission)
      })

}, (_, partial) => partial.id /*resolver*/, 6000 /*delay*/, { maxWait: 60000 })


/**
 * Directly flush submission for given partial
 * If flush fails, retry last sync and immediately flush again
 *
 * @param  {Object} partial
 * @return {Promise}
 */
export const flushSyncSubmission = (partial) => syncSubmission.flush(null, partial)
    .catch( () => {
        syncSubmission.retry(null, partial)
        return syncSubmission.flush(null, partial)
    })

const getExistingSubmission = ({ state }, partial, progress, revision) => {
    return partial.submissions.map( id => state.submissions.storage[id] )
        .find( s => s.progress == progress.id && s.revision === revision )
}

const addTempSubmission = ({ dispatch }, partial, progress, revision, data) => {

    // create temporary submission with fake id
    const submission = {
        id: guid(),
        data: data,
        progress: progress.id,
        partial: partial.id,
        isFrozen: false,
        revision: revision,
        _temp: true,
    }

    dispatch('SET_SUBMISSION', submission);
    dispatch('ADD_SUBMISSION_TO_PARTIAL', partial.id, submission.id)

    if ( partial.primary ) {
        dispatch('ADD_PRIMARY_SUBMISSION_TO_PROGRESS', progress.id, submission.id)
    }

    return submission
}


export const deleteSubmission = ({ dispatch }, partial, submissionId, progress) => {

    return api.deleteSubmission(submissionId).then( response => {

        dispatch('REMOVE_SUBMISSION_FROM_PARTIAL', partial.id, submissionId)
        dispatch('REMOVE_SUBMISSION', submissionId)
        if ( partial.primary ) {
            dispatch('REMOVE_PRIMARY_SUBMISSION_FROM_PROGRESS', progress.id, submissionId)
        }

        return response

    }, error => logreject('Could not delete submission', error))

}



// WEBPACK FOOTER //
// ./resources/assets/js/vuex/actions.js